import React, { useMemo, useState } from 'react';
import {
  useFloating, autoUpdate, offset, flip, shift, useClick, useDismiss,
  useRole, useInteractions, FloatingPortal, FloatingFocusManager,
} from '@floating-ui/react';
import { PRESETS } from '../data.js';
import { formatSourceStateTooltip, normalizeSourceState } from '../source-state.js';
import { signatureOfProfile, signatureOfScenario, diffScenarios } from '../scenario-sig.js';

/* v8 Path F — ScenarioStatus
   Topbar readout of which model state is active (loaded profile name or the
   default Base preset), whether the user has drifted from it (dirty dot),
   and a click-to-open popover listing exactly what changed. This replaces
   the need for users to hand-write heavy descriptions when saving — the
   diff IS the description. */
const LABEL = 'text-[0.65rem] text-text-dim uppercase tracking-[0.06em]';

export default function ScenarioStatus({ scenario, sourceState, onJumpToSettings }) {
  const [open, setOpen] = useState(false);
  const normalizedSource = normalizeSourceState(sourceState);
  const sourceTooltip = formatSourceStateTooltip(normalizedSource);
  const { refs, floatingStyles, context } = useFloating({
    open, onOpenChange: setOpen,
    placement: 'bottom-end',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const profiles = scenario.profiles || {};
  const active = scenario.activeProfile;
  const activeProfile = active ? profiles[active] : null;

  /* The "baseline" to compare against for dirty detection is either the
     loaded profile (if any) or the PRESETS.base default. Everything else
     downstream (diff list, labels) falls out of that choice. */
  const baseline = useMemo(() => activeProfile || PRESETS.base, [activeProfile]);
  const baselineLabel = activeProfile ? activeProfile.name : 'Default · Base';

  const baselineSig = useMemo(
    () => activeProfile ? signatureOfProfile(activeProfile) : signatureOfScenario({ ...PRESETS.base }),
    [activeProfile],
  );
  const scenarioSig = useMemo(() => signatureOfScenario(scenario), [scenario]);
  const isDirty = baselineSig !== scenarioSig;

  const changes = useMemo(() => {
    if (!isDirty) return [];
    return diffScenarios(baseline, scenario);
  }, [isDirty, baseline, scenario]);

  return (
    <>
      <button
        type="button"
        ref={refs.setReference}
        {...getReferenceProps()}
        className={`flex flex-col leading-tight gap-[2px] px-[0.7rem] py-[0.15rem] rounded-sm cursor-pointer border transition-colors text-left ${isDirty
          ? 'border-accent-gold/60 bg-[rgba(240,180,41,0.06)] hover:bg-[rgba(240,180,41,0.12)]'
          : 'border-border bg-transparent hover:border-border-strong'}`}
        title={isDirty
          ? `${changes.length} change${changes.length === 1 ? '' : 's'} from ${baselineLabel} — click for details. ${sourceTooltip}`
          : `Matches ${baselineLabel}. ${sourceTooltip}`}
        aria-label="Model status"
      >
        <span className={LABEL}>{activeProfile ? 'Private profile' : 'Private baseline'}</span>
        <span className="flex items-center gap-[6px] text-[0.82rem]">
          <strong className="text-text">{baselineLabel}</strong>
          {isDirty && (
            <>
              <span className="inline-block w-[6px] h-[6px] rounded-full bg-accent-gold shadow-[0_0_6px_rgba(240,180,41,0.6)]" aria-hidden />
              <em className="text-accent-gold not-italic text-[0.7rem] font-semibold">
                modified · {changes.length}
              </em>
            </>
          )}
        </span>
      </button>

      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
                width: 'min(480px, calc(100vw - 16px))',
                maxWidth: 'calc(100vw - 16px)',
                overflowWrap: 'anywhere',
              }}
              {...getFloatingProps()}
              className="z-[160] min-w-0 max-h-[60vh] overflow-y-auto overflow-x-hidden bg-surface-el border border-border-strong rounded-md shadow-lg py-[0.6rem] px-[0.85rem]"
            >
              <header className="flex min-w-0 items-baseline justify-between gap-2 pb-[0.4rem] border-b border-border mb-[0.5rem]">
                <div className="min-w-0">
                  <div className="text-[0.7rem] uppercase tracking-[0.06em] text-text-dim">Compared against local browser state</div>
                  <div className="text-[0.9rem] font-semibold text-text break-words">{baselineLabel}</div>
                </div>
                <span className={`text-[0.7rem] font-mono px-[6px] py-px rounded-[3px] ${isDirty
                  ? 'bg-[rgba(240,180,41,0.14)] text-accent-gold border border-[rgba(240,180,41,0.35)]'
                  : 'bg-white/[0.04] text-text-dim border border-border'}`}>
                  {isDirty ? `${changes.length} change${changes.length === 1 ? '' : 's'}` : 'clean'}
                </span>
              </header>

              {!isDirty && (
                <p className="text-[0.78rem] text-text-muted m-0">
                  Model state matches the {activeProfile ? 'loaded private browser profile' : 'default local preset'} exactly.
                </p>
              )}

              {isDirty && (
                <>
                  <ul className="list-none p-0 m-0 flex flex-col gap-[0.35rem]">
                    {changes.map((c) => (
                      <li
                        key={c.path}
                        className="grid grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)] gap-[0.5rem] items-baseline px-[0.35rem] py-[0.3rem] bg-white/[0.02] border border-border rounded-sm"
                      >
                        <span className="font-mono text-[0.72rem] text-accent-gold break-all">{c.path}</span>
                        <span className="text-[0.78rem] text-text tabular-nums break-words">{c.summary}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[0.72rem] text-text-dim mt-[0.6rem] mb-0 italic leading-relaxed">
                    Private browser session only — reloading the app discards these changes unless you save them as a local Model Profile.
                  </p>
                  <div className="flex gap-2 mt-[0.6rem]">
                    <button
                      type="button"
                      className="bg-[rgba(240,180,41,0.10)] border border-accent-gold text-accent-gold px-[0.7rem] py-[0.3rem] rounded-sm cursor-pointer text-[0.75rem] font-[inherit] hover:bg-[rgba(240,180,41,0.18)]"
                      onClick={() => { setOpen(false); onJumpToSettings?.(); }}
                    >
                      Save private profile →
                    </button>
                  </div>
                </>
              )}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
